/**
 * Result shape returned by mock OData paging/filtering/sorting.
 */
export interface MockODataPagingResult {
  data: Array<Record<string, unknown>>;
  value: Array<Record<string, unknown>>;
  totalRecords: number;
  "@odata.count": number;
  "@odata.context": string;
  pageNumber: number;
  pageSize: number;
  serverSidePaging: true;
}

/**
 * Returns true when a request appears to be using OData query params or path.
 */
export function isLikelyODataRequest(url: URL): boolean {
  if (url.pathname.toLowerCase().includes("/odata/")) {
    return true;
  }

  const odataKeys = ["$filter", "$orderby", "$top", "$skip", "$count"];
  return odataKeys.some((key) => url.searchParams.has(key));
}

/**
 * Applies a simplified OData subset to an array dataset.
 * Supported: `$filter`, `$orderby`, `$top`, `$skip`, `$count`.
 */
export function getODataMockPagingResult(url: URL, dataset: unknown): MockODataPagingResult {
  const rows = Array.isArray(dataset) ? [...dataset as Array<Record<string, unknown>>] : [];
  const filterExpr = url.searchParams.get("$filter") ?? "";
  const orderByExpr = url.searchParams.get("$orderby") ?? "";
  const top = parseNonNegativeInt(url.searchParams.get("$top"), 20);
  const skip = parseNonNegativeInt(url.searchParams.get("$skip"), 0);

  const filtered = applyODataFilter(rows, filterExpr);
  const sorted = applyODataOrderBy(filtered, orderByExpr);
  const paged = sorted.slice(skip, skip + top);
  const pageNumber = top > 0 ? Math.floor(skip / top) + 1 : 1;
  const pageSize = top > 0 ? top : 20;
  const totalRecords = sorted.length;

  return {
    data: paged,
    value: paged,
    totalRecords,
    "@odata.count": totalRecords,
    "@odata.context": "mock/$metadata#collection",
    pageNumber,
    pageSize,
    serverSidePaging: true,
  };
}

/**
 * Parses a non-negative integer with fallback.
 */
function parseNonNegativeInt(value: string | null, fallback: number): number {
  const parsed = Number.parseInt(value ?? "", 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

/**
 * Applies a simplified `$filter` expression.
 */
function applyODataFilter(rows: Array<Record<string, unknown>>, filterExpr: string): Array<Record<string, unknown>> {
  const expr = filterExpr.trim();
  if (!expr.length) {
    return rows;
  }

  const andClauses = splitByLogical(expr, "and");
  return rows.filter((row) => andClauses.every((clause) => evaluateClause(row, clause)));
}

/**
 * Applies a simplified `$orderby` expression.
 */
function applyODataOrderBy(rows: Array<Record<string, unknown>>, orderByExpr: string): Array<Record<string, unknown>> {
  const expr = orderByExpr.trim();
  if (!expr.length) {
    return rows;
  }

  const sorters = expr.split(",").map((part) => part.trim()).filter(Boolean).map((part) => {
    const [field, direction] = part.split(/\s+/);
    return { field, direction: direction?.toLowerCase() === "desc" ? "desc" : "asc" as "asc" | "desc" };
  });

  if (!sorters.length) {
    return rows;
  }

  return [...rows].sort((a, b) => {
    for (const sorter of sorters) {
      const cmp = compareUnknown(a[sorter.field], b[sorter.field]);
      if (cmp !== 0) {
        return sorter.direction === "desc" ? -cmp : cmp;
      }
    }
    return 0;
  });
}

/**
 * Evaluates one clause which may contain OR conditions.
 */
function evaluateClause(row: Record<string, unknown>, clause: string): boolean {
  const trimmed = stripOuterParens(clause.trim());
  const orParts = splitByLogical(trimmed, "or");
  return orParts.some((part) => evaluateAtomicExpression(row, part.trim()));
}

/**
 * Evaluates a single atomic OData expression.
 */
function evaluateAtomicExpression(row: Record<string, unknown>, expr: string): boolean {
  const containsMatch = expr.match(/^contains\(\s*([A-Za-z0-9_.]+)\s*,\s*(.+)\)$/i);
  if (containsMatch) {
    const field = containsMatch[1];
    const rhs = parseLiteral(containsMatch[2]);
    const value = row[field];
    return typeof value === "string" && typeof rhs === "string"
      ? value.toLowerCase().includes(rhs.toLowerCase())
      : false;
  }

  const compareMatch = expr.match(/^([A-Za-z0-9_.]+)\s+(eq|gt|ge|lt|le)\s+(.+)$/i);
  if (!compareMatch) {
    return false;
  }

  const field = compareMatch[1];
  const operator = compareMatch[2].toLowerCase();
  const rhs = parseLiteral(compareMatch[3]);
  const lhs = row[field];

  return compareByOperator(lhs, rhs, operator);
}

/**
 * Compares two values using an OData comparison operator.
 */
function compareByOperator(lhs: unknown, rhs: unknown, operator: string): boolean {
  if (lhs === null || lhs === undefined) {
    return false;
  }

  if (typeof lhs === "string" && typeof rhs === "string") {
    const maybeLhsDate = Date.parse(lhs);
    const maybeRhsDate = Date.parse(rhs);
    if (!Number.isNaN(maybeLhsDate) && !Number.isNaN(maybeRhsDate)) {
      return compareNumbers(maybeLhsDate, maybeRhsDate, operator);
    }
    return compareStrings(lhs, rhs, operator);
  }

  if (typeof lhs === "number") {
    const rhsNumber = typeof rhs === "number" ? rhs : Number(rhs);
    return Number.isFinite(rhsNumber) && compareNumbers(lhs, rhsNumber, operator);
  }

  if (typeof lhs === "boolean" && typeof rhs === "boolean") {
    if (operator !== "eq") {
      return false;
    }
    return lhs === rhs;
  }

  if (operator !== "eq") {
    return false;
  }

  return String(lhs).toLowerCase() === String(rhs).toLowerCase();
}

/**
 * Compares two numbers by the specified operator.
 */
function compareNumbers(lhs: number, rhs: number, operator: string): boolean {
  switch (operator) {
    case "eq":
      return lhs === rhs;
    case "gt":
      return lhs > rhs;
    case "ge":
      return lhs >= rhs;
    case "lt":
      return lhs < rhs;
    case "le":
      return lhs <= rhs;
    default:
      return false;
  }
}

/**
 * Compares strings with case-insensitive semantics.
 */
function compareStrings(lhs: string, rhs: string, operator: string): boolean {
  const left = lhs.toLowerCase();
  const right = rhs.toLowerCase();
  switch (operator) {
    case "eq":
      return left === right;
    case "gt":
      return left > right;
    case "ge":
      return left >= right;
    case "lt":
      return left < right;
    case "le":
      return left <= right;
    default:
      return false;
  }
}

/**
 * Parses a filter literal from OData text.
 */
function parseLiteral(value: string): unknown {
  const trimmed = value.trim();
  const quoted = trimmed.match(/^'(.*)'$/s);
  if (quoted) {
    return quoted[1].replace(/''/g, "'");
  }

  const boolLiteral = trimmed.toLowerCase();
  if (boolLiteral === "true") {
    return true;
  }
  if (boolLiteral === "false") {
    return false;
  }

  const numberValue = Number(trimmed);
  if (Number.isFinite(numberValue)) {
    return numberValue;
  }

  return trimmed;
}

/**
 * Splits by top-level `and`/`or` while respecting parentheses depth.
 */
function splitByLogical(expr: string, keyword: "and" | "or"): string[] {
  const parts: string[] = [];
  let depth = 0;
  let token = "";

  for (let i = 0; i < expr.length; i++) {
    const char = expr[i];
    if (char === "(") {
      depth += 1;
      token += char;
      continue;
    }
    if (char === ")") {
      depth = Math.max(0, depth - 1);
      token += char;
      continue;
    }

    if (depth === 0 && startsWithLogical(expr, i, keyword)) {
      const trimmed = token.trim();
      if (trimmed.length) {
        parts.push(trimmed);
      }
      token = "";
      i += keyword.length + 1; // Skip " keyword "
      continue;
    }

    token += char;
  }

  const last = token.trim();
  if (last.length) {
    parts.push(last);
  }
  return parts;
}

/**
 * Checks whether `expr` contains ` keyword ` at index.
 */
function startsWithLogical(expr: string, index: number, keyword: "and" | "or"): boolean {
  const segment = expr.slice(index, index + keyword.length + 2);
  return segment.toLowerCase() === ` ${keyword} `;
}

/**
 * Removes one balanced outer pair of parentheses if present.
 */
function stripOuterParens(value: string): string {
  if (!value.startsWith("(") || !value.endsWith(")")) {
    return value;
  }

  let depth = 0;
  for (let i = 0; i < value.length; i++) {
    const char = value[i];
    if (char === "(") {
      depth += 1;
    } else if (char === ")") {
      depth -= 1;
      if (depth === 0 && i < value.length - 1) {
        return value;
      }
    }
  }

  return value.slice(1, -1);
}

/**
 * Compares unknown values for sorting.
 */
function compareUnknown(a: unknown, b: unknown): number {
  if (a === b) {
    return 0;
  }
  if (a === null || a === undefined) {
    return 1;
  }
  if (b === null || b === undefined) {
    return -1;
  }

  if (typeof a === "string" && typeof b === "string") {
    return a.localeCompare(b, undefined, { sensitivity: "base" });
  }
  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }
  if (typeof a === "boolean" && typeof b === "boolean") {
    return Number(a) - Number(b);
  }

  return String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
}
